// Phase G4-A — skill metadata lookups (domain + ageBand).
//
// ChildAttempt + AdaptationDecisionInput need a SkillDomain and an
// AgeBand for a given skillId. microSkills.json carries `ageBand` +
// `strand` (math/reading/writing); the engine's SkillDomain union is
// finer-grained than strand, so this module maps a skill to its
// domain using the learningProgressions primaryChannel + skill family.
//
// Scoped to what the G4 slice needs (ms.math.09 → math_operations,
// ms.read.06 → phonics_decoding) plus sensible fallbacks for the
// other microskills so the engine doesn't crash on a non-slice skill
// (it'll still gate to safe-fallback via content safety, but the
// input must be well-formed).

import type { AgeBand, ContentAudience, MasteryChannel, SkillDomain } from "@/adaptation/types";
import { ageBandRow } from "@/adaptation/types";
import { inferChannel } from "@/adaptation/masteryClassInference";
import microSkills from "@/curriculum/data/manifests/microSkills.json" with { type: "json" };

interface MicroSkillRow {
  id: string;
  ageBand: AgeBand;
  strand: "math" | "reading" | "writing";
  label: string;
}

const BY_ID: Record<string, MicroSkillRow> = (() => {
  const map: Record<string, MicroSkillRow> = {};
  for (const s of microSkills as MicroSkillRow[]) {
    map[s.id] = s;
  }
  return map;
})();

/** AgeBand for a skill; falls back to the mid band if unknown. */
export function ageBandForSkill(skillId: string): AgeBand {
  return BY_ID[skillId]?.ageBand ?? "5.5-6.0";
}

// Channel → SkillDomain. The primaryChannel from learningProgressions
// (inferChannel) determines the domain. Reading-decoding skills are
// phonics_decoding; math-concept skills are math_operations; etc.
export const CHANNEL_TO_DOMAIN: Record<MasteryChannel, SkillDomain> = {
  readingDecodingMastery: "phonics_decoding",
  independentReadingMastery: "decodable_connected_text",
  oralVocabularyMastery: "vocabulary_semantics",
  listeningComprehensionMastery: "reading_comprehension",
  semanticLanguageMastery: "vocabulary_semantics",
  grammarSentenceMastery: "grammar_sentence_language",
  mathConceptMastery: "math_operations",
  mathReasoningMastery: "math_word_problems",
  // Grade-2 ceiling (Phase 0.3): multiplicative reasoning (skip-count,
  // equal groups, arrays, division-as-sharing) is its OWN domain so it
  // never mis-classifies as additive math_operations. Edited atomically
  // with the MasteryChannel union + SkillDomain union (the load-bearing
  // lockstep trap). Guarded by taxonomy-routing.test.mjs.
  multiplicativeReasoning: "math_multiplicative",
  // Grade-2 ceiling (Wave 2-A): money / time / measurement → the dormant
  // math_geometry_measurement domain (which already routes to mathExemplars
  // in every primaryPackForSkill switch). Edited atomically with the
  // MasteryChannel union (adaptationTypes.ts). `schemas/manifests.ts`'s
  // MASTERY_CHANNELS schema list, once the trap's third artefact, was
  // deleted by 353bb1c9 (MA-CL-6 knip round 2 (b)); a typo'd
  // learningProgressions.json primaryChannel is now caught at runtime by
  // validateProgressionChannels() (curriculum/validators/routingValidators.ts,
  // run by test:curriculum-validation) against Object.keys(CHANNEL_TO_DOMAIN)
  // below (MA-630 §4f). Guarded by money-time-routing.test.mjs.
  measurementGeometryMastery: "math_geometry_measurement",
  handwritingMastery: "spelling_encoding",
  numeralFormationMastery: "math_number_sense",
};

/** SkillDomain for a skill, derived from its primary mastery channel.
 * ms.math.09 → mathConceptMastery → math_operations.
 * ms.read.06 → readingDecodingMastery → phonics_decoding. */
export function domainForSkill(skillId: string): SkillDomain {
  const channel = inferChannel(skillId);
  return CHANNEL_TO_DOMAIN[channel] ?? "math_operations";
}

/** SkillDomain for a mastery channel directly (no skill lookup). Exposed
 *  so the taxonomy-routing guard test can assert the channel→domain
 *  lockstep without needing an authored skill for every channel. */
export function domainForChannel(channel: MasteryChannel): SkillDomain {
  return CHANNEL_TO_DOMAIN[channel] ?? "math_operations";
}

// ── P-METADATA-EXPAND-A helpers ──────────────────────────────────────

/** Subject ID — the COARSE strand bucket the parent dashboard groups
 *  by. Sourced directly from microSkills.json's `strand` field.
 *  Returns null when the skill is not in the manifest (so callers
 *  serialize NULL rather than fabricating a strand).
 *
 *  NB: "foundation" + "writing" are PART-B values reserved for future
 *  delivery modes; this helper currently only emits "math" | "reading"
 *  | "writing" matching the MicroSkillRow.strand union. Foundation is
 *  a delivery mode (letter formation, handwriting) — its skills are
 *  ms.read.* prefix, so they correctly classify as "reading" here. */
export function subjectForSkill(
  skillId: string,
): "math" | "reading" | "foundation" | "writing" | null {
  const strand = BY_ID[skillId]?.strand;
  if (!strand) return null;
  return strand;
}

/** Foundation-DELIVERED skills (letter names + letter formation).
 *  Tap targets that resolve to one of these route to /play/foundation
 *  (FoundationSession's letter renderers), NOT /play/reading — the
 *  fill-blank sentence renderer has no content for them. Hoisted from
 *  StorybookJourney (RC-R, §K fold B.2) so the trail CTA (PathHome) and
 *  the journey stones share ONE list. NB: these are still strand
 *  "reading" per subjectForSkill — foundation is a delivery mode, not
 *  a trail row (see the milestoneLogger subject contract). */
export const FOUNDATION_SKILL_IDS: readonly string[] = [
  "ms.read.01",
  // MA-671 slice 1b (2026-09-19): letter sounds (identifyLetterForSound,
  // same multiple-choice/tap_choice shape ms.read.01's identifyNamedLetter
  // items use) has no renderer branch on ReadingSession — FoundationSession
  // is the real screen that already serves this shape. Routed alongside
  // ms.read.01/03 (the PACK_OVERRIDE entry in packRouting.ts is the
  // matching content-pack half of this same slice).
  "ms.read.02",
  "ms.read.03",
  // MA-671 slice 2b (2026-09-21): ms.read.05 (Oral segmenting,
  // phonemeCount — "How many sounds do you hear in <word>?") has no
  // renderer branch on ReadingSession's fill-blank cloze path either (its
  // prompt is a whole question, not a sentence template); FoundationSession
  // is where the identical multiple-choice/tap_choice shape already renders
  // (same ChoiceTiles branch ms.read.01/02 use). Routed alongside them (the
  // PACK_OVERRIDE entry in packRouting.ts is the matching content-pack half
  // of this same slice). ms.read.05 stays OUT of chapters.json — see
  // packRouting.ts's own comment for the two live reachability channels.
  "ms.read.05",
  // T2-D: CVC encoding (sound-box) is delivered via FoundationSession's
  // sound_box_encode branch (scaffolded). Routing it to /play/reading would
  // mount the fill-blank chip grid, which has no encode interaction.
  "ms.read.07",
  // MA-126: ms.read.04 is the same sound-box shape as ms.read.07 (ch1
  // supportSkill); same routing requirement.
  "ms.read.04",
  // MA-208(c1): sight-word tracing (trace_target modality) — same
  // FoundationSession routing requirement as the sound-box skills above.
  "ms.read.dolch_trace",
];

/** The audience a skill is ACTUALLY servable under, for pool-size gating
 *  (MA-134). FOUNDATION_SKILL_IDS' sound-box encode skills (ms.read.04/07)
 *  are design-locked scaffolded-only — their independent_practice pool is
 *  INTENTIONALLY, PERMANENTLY 0 (ADR-055; grading sound-box copy as
 *  independent mastery would be a completion-graded task, not real
 *  evidence) even though FoundationSession serves them 144/150 items wide
 *  under "scaffolded" every day. Any caller that asks "does this skill have
 *  servable content" (the SkillTree Assign gate, the assigned-queue focal
 *  pick) must gate on THIS audience, not blindly default to
 *  independent_practice, or a skill kids actively practice reads as dead. */
export function audienceForSkill(skillId: string): ContentAudience {
  return FOUNDATION_SKILL_IDS.includes(skillId) ? "scaffolded" : "independent_practice";
}

/** Passage-bearing reading skills (decodablePassages / comprehensionQuestions
 *  packs, MA-125). Every launch_safe item on these skills carries
 *  `passageBodySentences` that the item is unanswerable without — only
 *  `PassageReader` (ReadingSession.tsx) renders it. Any screen that mounts
 *  reading content without a PassageReader (MixedSession has none) must
 *  exclude these ids from its candidate set, or it serves a comprehension
 *  question with no story to answer it from. */
export const PASSAGE_BEARING_SKILL_IDS: readonly string[] = [
  "ms.read.13",
  "ms.read.18",
  "ms.read.19",
  "ms.read.20",
];

/** Human-readable skill name for parent-facing surfaces (e.g.
 *  "Count objects to 5"). Sourced directly from microSkills.json's `label`.
 *  Falls back to the raw skillId when the skill is not in the manifest, so a
 *  stray id renders something stable rather than throwing. */
export function labelForSkill(skillId: string): string {
  return BY_ID[skillId]?.label ?? skillId;
}

/** Grade-band label for an age band (US K-12 conventions: Pre-K is
 *  pre-kindergarten ~age 4; K is ages 5-6; G1 is ages 6-7; G2 is
 *  ages 7-8). CALIBRATION TARGET — the band labels are mapped to the
 *  parent-dashboard taxonomy; the band taxonomy is content-team
 *  owned and the column in 0006 is open-ended TEXT (no CHECK) so the
 *  taxonomy can expand without a schema migration. */
export function gradeBandForAgeBand(ageBand: AgeBand): string {
  // Phase 0.1 — grade band now lives in the ordered band table.
  return ageBandRow(ageBand).gradeBand;
}
